import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

const DIRECTION_LABEL = {
    "add_to_bank": "Add to Bank",
    "less_from_bank": "Less from Bank",
    "add_to_book": "Add to Book",
    "less_from_book": "Less from Book",
};
const DIRECTION_COLOR = {
    "add_to_bank": "#4af6c3",
    "less_from_bank": "#ff433d",
    "add_to_book": "#4af6c3",
    "less_from_book": "#ff433d",
};

export default function Reconciliation() {
    const [data, setData] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        api.get("/reconciliation").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING RECONCILIATION...</div>;

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// BANK RECONCILIATION</div>
                <h1 className="font-display font-black text-3xl">Book vs Bank</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    As of 15-Feb-2026 · <Link to="/banking" className="text-[var(--brand-amber)] hover:underline">Banking →</Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="recon-book" label="Total Book Balance" value={data.summary.total_book} sub="Ledger" />
                <KpiCard testId="recon-bank" label="Total Bank Balance" value={data.summary.total_bank} sub="Statement" />
                <KpiCard testId="recon-diff" label="Gross Difference" value={data.summary.gross_difference} sub={`${data.summary.total_open_items} open items`} accent />
                <KpiCard testId="recon-status" label="Reconciled Accounts" value={`${data.summary.reconciled_accounts} / ${data.summary.total_accounts}`} format="custom" sub="Balanced" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                {data.statements.map((s) => (
                    <div key={s.account_id} className="bg-panel border border-panel" data-testid={`recon-card-${s.account_id}`}>
                        <div className="px-4 py-3 border-b border-panel flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    {s.status === "Reconciled"
                                        ? <CheckCircle2 size={14} className="text-pos" />
                                        : <AlertTriangle size={14} className="text-amber" />}
                                    <span className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--text-primary)]">{s.bank}</span>
                                    <span className="text-[10px] font-mono-fin text-[var(--text-secondary)]">{s.acc_no}</span>
                                </div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] mt-0.5">{s.branch} · As of {fmtDate(s.as_of)}</div>
                            </div>
                            <span className={`text-[10px] font-mono-fin uppercase tracking-wider px-2 py-1 border`} style={{ borderColor: s.status === "Reconciled" ? "#4af6c3" : "#fb8b1e", color: s.status === "Reconciled" ? "#4af6c3" : "#fb8b1e" }}>
                                {s.status}
                            </span>
                        </div>

                        <div className="p-4 grid grid-cols-3 gap-3 border-b border-panel">
                            <div>
                                <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)]">Book</div>
                                <div className={`font-mono-fin text-lg mt-1 ${s.book_balance >= 0 ? "text-[var(--text-primary)]" : "text-neg"}`}>{fmtINR(s.book_balance)}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)]">Bank</div>
                                <div className={`font-mono-fin text-lg mt-1 ${s.bank_balance >= 0 ? "text-[var(--text-primary)]" : "text-neg"}`}>{fmtINR(s.bank_balance)}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)]">Difference</div>
                                <div className={`font-mono-fin text-lg mt-1 ${Math.abs(s.difference) < 100 ? "text-pos" : "text-amber"}`}>{fmtINR(s.difference)}</div>
                            </div>
                        </div>

                        {s.items.length > 0 ? (
                            <div className="p-4">
                                <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-2">
                                    Reconciling Items ({s.items.length})
                                </div>
                                <div className="divide-y divide-[#E2E8F0]">
                                    {s.items.map((it) => (
                                        <div key={it.id} className="py-2 flex items-start gap-3">
                                            <ArrowRight size={12} className="mt-1 shrink-0" style={{ color: DIRECTION_COLOR[it.direction] }} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-[var(--text-primary)]">{it.type}</div>
                                                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mt-0.5">{it.party} · {it.ref} · {fmtDate(it.date)}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-mono-fin text-xs text-[var(--text-primary)]">{fmtINR(it.amount)}</div>
                                                <div className="text-[9px] font-mono-fin uppercase tracking-wider mt-0.5" style={{ color: DIRECTION_COLOR[it.direction] }}>{DIRECTION_LABEL[it.direction]}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-3 pt-3 border-t border-panel text-[10px] font-mono-fin uppercase tracking-wider flex justify-between">
                                    <span className="text-[var(--text-secondary)]">Adjusted Book Balance</span>
                                    <span className="text-amber">{fmtINR(s.adjusted_book)}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 text-[11px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
                                <CheckCircle2 size={12} className="text-pos" /> No open items · Fully reconciled
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <Panel testId="recon-legend" title="Reconciliation Legend">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs font-body">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-pos mb-1">Add to Bank</div>
                        <div className="text-[var(--text-secondary)]">Cheques issued but not yet presented (outflow booked, bank still has money)</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-neg mb-1">Less from Bank</div>
                        <div className="text-[var(--text-secondary)]">Cheques deposited but not yet credited (inflow booked, bank hasn't received)</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-pos mb-1">Add to Book</div>
                        <div className="text-[var(--text-secondary)]">Bank credits not yet in ledger (interest, direct receipts)</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-neg mb-1">Less from Book</div>
                        <div className="text-[var(--text-secondary)]">Bank debits not yet in ledger (charges, standing instructions)</div>
                    </div>
                </div>
            </Panel>
        </div>
    );
}
